import { CircleCheck, CircleXIcon } from 'lucide-react'
import * as React from 'react'

import { Popover, PopoverAnchor, PopoverContent } from '#components/ui/popover'
import { cn } from '#lib/utils'

interface InlinePasswordPopoverProps {
  isOpen: boolean
  password: string
  anchorRef: React.RefObject<HTMLDivElement | null>
}

export const InlinePasswordPopover = ({
  isOpen,
  password,
  anchorRef,
}: InlinePasswordPopoverProps) => {
  const DEFAULT_PASSWORD_CONFIRMITY = {
    min8Char: false,
    max20Char: false,
    min1Number: false,
    min1Upper: false,
    min1Lower: false,
    min1Special: false,
  }

  const [conformity, setConformity] = React.useState(DEFAULT_PASSWORD_CONFIRMITY)

  React.useEffect(() => {
    setConformity({
      min8Char: password.length >= 8,
      max20Char: password.length <= 20 && password.length > 0,
      min1Number: /[0-9]/.test(password),
      min1Upper: /[A-Z]/.test(password),
      min1Lower: /[a-z]/.test(password),
      min1Special: /[^a-zA-Z0-9]/.test(password),
    })
  }, [password])

  const Item = ({ ok, label }: { ok: boolean; label: string }) => (
    <div className="flex items-center gap-2">
      {ok ? <CircleCheck color="#1D2CB6" /> : <CircleXIcon />}
      <p className="text-sm font-normal text-gray-950">{label}</p>
    </div>
  )

  return (
    <Popover open={isOpen} modal={false}>
      <PopoverAnchor asChild>
        <div ref={anchorRef} />
      </PopoverAnchor>

      <PopoverContent
        onOpenAutoFocus={(e) => {
          e.preventDefault()
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault()
        }}
        side="bottom"
        align="start"
        sideOffset={12}
        className={cn(
          'relative z-40 rounded-xl border border-gray-200 bg-white px-8 py-4 shadow-xl',
          'animate-in fade-in slide-in-from-top-2 duration-200',
          'w-[var(--radix-popover-trigger-width)]'
        )}
      >
        <div className="flex gap-4">
          <div className="flex grow flex-col items-start gap-4">
            <Item ok={conformity.min8Char} label="min 8 caractères" />
            <Item ok={conformity.max20Char} label="max 20 caractères" />
            <Item ok={conformity.min1Number} label="1 chiffre" />
          </div>

          <div className="flex grow flex-col items-start gap-4">
            <Item ok={conformity.min1Upper} label="1 majuscule" />
            <Item ok={conformity.min1Lower} label="1 minuscule" />
            <Item ok={conformity.min1Special} label="1 caractère spécial" />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
